import React from 'react';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import AssessmentIcon from '@mui/icons-material/Assessment';
import BarChartIcon from '@mui/icons-material/BarChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CategoryIcon from '@mui/icons-material/Category';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';

export const SEVERITY = { CRITICAL: 'Critical', WARNING: 'Warning', INFO: 'Info' };

export const SEVERITY_COLOR = {
    [SEVERITY.CRITICAL]: '#dc3545',
    [SEVERITY.WARNING]: '#ff9800',
    [SEVERITY.INFO]: '#1976d2',
};

export const hasReportProblems = (description = '') => {
    const problemsSection = /### Problems Found\s*([\s\S]*?)(?=\n###|$)/i.exec(description)?.[1] || '';
    return /^\s*-\s+`[^`]+`:/m.test(problemsSection);
};

export const severityIcon = (severity) => {
    const color = SEVERITY_COLOR[severity] || SEVERITY_COLOR[SEVERITY.INFO];
    const sx = { color: `${color} !important` };
    switch (severity) {
        case SEVERITY.CRITICAL: return <ErrorIcon sx={sx} />;
        case SEVERITY.WARNING:  return <WarningIcon sx={sx} />;
        case SEVERITY.INFO:     return <InfoIcon sx={sx} />;
        default:                return <InfoIcon sx={sx} />;
    }
};

export const notificationTypeIcon = (type, sxOverride) => {
    const sx = { fontSize: 16, color: 'text.secondary', ...sxOverride };
    switch (type) {
        case 'alarm':   return <NotificationsActiveIcon sx={sx} />;
        case 'problem': return <ReportProblemIcon sx={sx} />;
        case 'report':  return <AssessmentIcon sx={sx} />;
        default:        return <NotificationsActiveIcon sx={sx} />;
    }
};

/**
 * Icon chosen from a notification's `source` field. Used as the dominant
 * left-side visual cue for alarms/problems so operators can tell at a glance
 * which producer service raised the notification (and therefore which
 * back-end API the drilldown will call).
 *
 * Per `notification-source-concept.md` §2.1, `source` identifies the
 * **producer service** (not the detector / evidence kind). Each producer
 * emits exactly one source slug:
 *
 *   `sli`               → BarChartIcon              (buc-services-yuk-sli)
 *   `yuki`              → AutoAwesomeIcon           (buc-services-yuki-agent;
 *                         same icon as the "Run Analysis" / generate-report
 *                         button on the Run Analysis page)
 *   `dr-events-analysis`→ SettingsInputAntennaIcon  (matches the "LCRs Management"
 *                         root menu icon in the left navigation drawer)
 *   anything else       → CategoryIcon              (manual-operator, future
 *                         producers, unknown values)
 *
 * Comparison is case-insensitive. `sxOverride` is merged onto the icon's `sx`
 * so callers can colour the icon (e.g. by severity).
 */
export const sourceIcon = (source, sxOverride) => {
    const sx = { ...sxOverride };
    const key = (source ?? '').toString().trim().toLowerCase();
    switch (key) {
        case 'sli':               return <BarChartIcon sx={sx} />;
        case 'yuki':              return <AutoAwesomeIcon sx={sx} />;
        case 'dr-events-analysis': return <SettingsInputAntennaIcon sx={sx} />;
        default:                  return <CategoryIcon sx={sx} />;
    }
};

/**
 * Icon used as the dominant visual cue for a notification (banner, list row).
 * For all notification types (alarm / problem / report), when a `source` is
 * present the icon is chosen from the source (see `sourceIcon`) so operators
 * can tell at a glance which producer service raised the row. Reports
 * therefore render with the same left icon as a problem from the same
 * producer (e.g. `source=yuki` → AutoAwesomeIcon), which matches operator
 * expectation that "report" is a sibling of "problem" rather than a different
 * visual category. Reports are tinted blue (`#007bc1`) — the report accent
 * colour — while reports containing problem entries use their severity colour.
 * Legacy rows without a source fall back to the per-severity icon.
 */
export const notificationDisplayIcon = (notification) => {
    if (!notification) return severityIcon(SEVERITY.INFO);
    if (notification.notificationType === 'report' && !hasReportProblems(notification.description)) {
        if (!notification.source) return <AssessmentIcon sx={{ color: '#007bc1 !important' }} />;
        return sourceIcon(notification.source, { color: '#007bc1 !important' });
    }
    if (notification.source) {
        const color = SEVERITY_COLOR[notification.severity] || SEVERITY_COLOR[SEVERITY.INFO];
        return sourceIcon(notification.source, { color: `${color} !important` });
    }
    return severityIcon(notification.severity);
};
